'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
} from '@marine/ui/avatar';
import { Button, ButtonArrow } from '@marine/ui/button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@marine/ui/popover';
import { ScrollArea } from '@marine/ui/scroll-area';

export type ComboboxUserItem = {
  id: string;
  name: string;
  email?: string | null;
  avatar?: string | null;
  status?: 'online' | 'offline' | 'busy' | 'away';
  /** #701 — optional trailing count. Omit and the row renders as before. */
  count?: number;
};

export interface ComboboxUserProps {
  users: ComboboxUserItem[];
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  includeUnassigned?: boolean;
  unassignedLabel?: string;
  /**
   * #745 — lands on the trigger so a `<Label htmlFor>` can point at it.
   *
   * Without this there was no way to associate a caption with the control:
   * every "Salesperson" label in the app was decorative text as far as a screen
   * reader was concerned, and clicking it did nothing. WCAG 2.1 AA, and
   * AGENTS.md requires it.
   */
  id?: string;
  /** For captions that cannot use `htmlFor` (e.g. a heading acting as the label). */
  'aria-labelledby'?: string;
  'aria-label'?: string;
}

const getInitials = (name?: string | null) => {
  if (!name) return 'U';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

export function ComboboxUser({
  users,
  value,
  onChange,
  placeholder = 'Select a user...',
  searchPlaceholder = 'Search user...',
  className,
  disabled,
  includeUnassigned = false,
  unassignedLabel = 'Unassigned',
  id,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
}: ComboboxUserProps) {
  const [open, setOpen] = React.useState(false);
  const selectedUser = users.find((user) => user.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          mode="input"
          placeholder={!value}
          aria-expanded={open}
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          {selectedUser ? (
            <span className="flex items-center gap-2">
              <Avatar className="size-6">
                {selectedUser.avatar ? (
                  <AvatarImage src={selectedUser.avatar} alt={selectedUser.name} />
                ) : (
                  <AvatarFallback>{getInitials(selectedUser.name)}</AvatarFallback>
                )}
                {selectedUser.status && (
                  <AvatarIndicator className="-end-2 -bottom-2">
                    <AvatarStatus variant={selectedUser.status} className="size-2.5" />
                  </AvatarIndicator>
                )}
              </Avatar>
              <span className="font-medium">{selectedUser.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ButtonArrow />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', className ? 'w-[--radix-popover-trigger-width]' : 'w-[300px]')}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <ScrollArea viewportClassName="max-h-[300px] [&>div]:block!">
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {includeUnassigned && (
                  <CommandItem
                    value={unassignedLabel}
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Avatar className="size-7">
                        <AvatarFallback>{getInitials(unassignedLabel)}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{unassignedLabel}</span>
                    </span>
                    <CommandCheck className={cn(!value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                )}
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.name} ${user.email ?? ''}`}
                    onSelect={() => {
                      onChange(user.id);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Avatar className="size-7">
                        {user.avatar ? (
                          <AvatarImage src={user.avatar} alt={user.name} />
                        ) : (
                          <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                        )}
                        {user.status && (
                          <AvatarIndicator className="-end-2 -bottom-2">
                            <AvatarStatus variant={user.status} className="size-2.5" />
                          </AvatarIndicator>
                        )}
                      </Avatar>
                      <span className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        {user.email && (
                          <span className="text-muted-foreground text-sm">{user.email}</span>
                        )}
                      </span>
                    </span>
                    {user.count !== undefined && (
                      <span className="ms-auto text-muted-foreground text-xs tabular-nums">
                        {user.count}
                      </span>
                    )}
                    <CommandCheck className={cn(value === user.id ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
